import React, { useCallback, useState } from "react";
import { addYears } from "date-fns";
import { FormattedMessage, useIntl } from "react-intl";
import { CalendarIcon } from "../../shared/assets/CalendarIcon";
import { ModalTitle, ModalAction } from "../../shared/styles";
import {
  CalendarBox,
  Container,
  DatesContainer,
  InputContainer,
  InputGroupContainer,
  StyledDatePicker,
  WeekdayContainer,
  StyledLabel,
  StyledCheckbox,
  ModalActionContainer, RemovePeriodButton,
} from "./styles";
import { messages } from "./messages";
import { DATE_FORMAT } from "../../shared/constants";
import { isAfterDates } from "../../helpers/dates";

interface Props {
  periods?: {
    startDate: Date,
    endDate: Date
  }[],
  onChangeFromDate: (date: Date, idx: number) => void;
  onChangeToDate: (date: Date, idx: number) => void;
  onChangeWeekday: (weekday: number, idx: number) => void;
  onAddPeriod: () => void;
  onRemovePeriod: (idx: number) => void;
  showWeekDays?: boolean;
  showAddPeriod?: boolean;
  disabled?: boolean;
  showOneDate?: boolean;
}

enum Weekdays {
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
  SUNDAY = "sunday"
}

const WeekdayCheckbox = ({ weekdayKey, onChange }: { weekdayKey: string, onChange: () => void }): JSX.Element => (
  <StyledLabel aria-label="weekday-checkbox">
    <StyledCheckbox type="checkbox" defaultChecked onChange={onChange} />
    <FormattedMessage {...messages[weekdayKey as keyof typeof messages]} />
  </StyledLabel>
);

export const DateRange = ({
  periods,
  onChangeFromDate,
  onChangeToDate,
  onChangeWeekday,
  onAddPeriod,
  onRemovePeriod,
  showWeekDays,
  showAddPeriod,
  disabled,
  showOneDate
}: Props): JSX.Element => {
  const intl = useIntl();
  const [quantity, setQuantity] = useState<number>(1);

  const handleAddPeriod = useCallback(() => {
    setQuantity(quantity + 1);
    onAddPeriod();
  }, [quantity, onAddPeriod]);

  const handleRemovePeriod = useCallback((idx: number) => {
    setQuantity(quantity - 1);
    onRemovePeriod(idx);
  }, [quantity, onRemovePeriod]);

  return (
    <Container>
      <ModalTitle>
        <FormattedMessage {...messages.dates} />
      </ModalTitle>

      {[...Array(quantity)].map((_value, idx) => {
        const start = (periods && periods[idx] && periods[idx].startDate) || new Date();
        let end = (periods && periods[idx] && periods[idx].endDate) || new Date();
        end = isAfterDates(start, end) ? new Date(start) : end;
        return (
          <DatesContainer key={idx}>
            <InputGroupContainer>

              {quantity > 1 && <RemovePeriodButton onClick={() => handleRemovePeriod(idx)} />}
              <InputContainer>
                <CalendarBox>
                  <CalendarIcon />
                </CalendarBox>
                <StyledDatePicker
                  selected={start}
                  placeholderText={intl.formatMessage(messages.fromDate)}
                  dateFormat={DATE_FORMAT.VISUAL}
                  minDate={new Date()}
                  maxDate={addYears(new Date(), 2)}
                  onChange={(newDate: Date) => onChangeFromDate(newDate, idx)}
                  disabled={disabled}
                />
              </InputContainer>

              {!showOneDate && (
                <InputContainer>
                  <CalendarBox>
                    <CalendarIcon />
                  </CalendarBox>
                  <StyledDatePicker
                    selected={end}
                    placeholderText={intl.formatMessage(messages.toDate)}
                    dateFormat={DATE_FORMAT.VISUAL}
                    minDate={start ? start : new Date()}
                    maxDate={addYears(new Date(), 2)}
                    onChange={(newDate: Date) => onChangeToDate(newDate, idx)}
                    disabled={disabled}
                  />
                </InputContainer>
              )}
            </InputGroupContainer>

            {showWeekDays && (
              <WeekdayContainer>
                {Object.values(Weekdays).map((weekday, weekdayIdx) => {
                  const weekdayNumber = weekdayIdx === 6 ? 0 : weekdayIdx + 1;
                  return (
                    <WeekdayCheckbox
                      key={`${weekday}-${weekdayIdx}`}
                      weekdayKey={weekday}
                      onChange={() => onChangeWeekday(weekdayNumber, idx)}
                    />
                  );
                })}
              </WeekdayContainer>
            )}
          </DatesContainer>
        );
      })}

      {showAddPeriod && (
        <ModalActionContainer>
          <ModalAction onClick={handleAddPeriod}>
            <FormattedMessage {...messages.addPeriod} />
          </ModalAction>
        </ModalActionContainer>
      )}
    </Container>
  );
};
